import { CareRecipientId } from "../../../domain/care_recipient/CareRecipientId";
import { Month } from "../../../domain/fundamental/Month";
import {
    getCareRecipientLatestInfo,
    getDailyComments,
    getDailyEstimatedBehavior,
    getEstimatedBehaviorAlert,
    getEstimatedBehaviorAlertPeriod,
    getEstimatedBehaviorAverages,
    getEstimatedBehaviorHistories,
    getMonthlyComments
} from "./api";
import { DateObject } from "../../../domain/fundamental/DateObject";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

export const ActionType = Object.freeze({
    base: "@@Monitoring/Action/Report",
    initMonthlyData: "initMonthlyData",
    getDailyComments: "getDailyComments",
    resetDailyComments: "resetDailyComments",
    getMonthlyComments: "getMonthlyComments",
    getDailyEstimatedBehavior: "getDailyEstimatedBehavior",
    getCareRecipientLatestInfo: "getCareRecipientLatestInfo",
    getEstimatedBehaviorHistories: "getEstimatedBehaviorHistories",
    getLastMonthEstimatedBehaviorHistories: "getLastMonthEstimatedBehaviorHistories",
    setPeriod: "setPeriod",
    getEstimatedBehaviorAverage: "getEstimatedBehaviorAverage",
    getEstimatedBehaviorAlert: "getEstimatedBehaviorAlert",
    getEstimatedBehaviorAlertPeriod: "getEstimatedBehaviorAlertPeriod"
});

export const initMonthlyDataAction = createAction(`${ActionType.base}/${ActionType.initMonthlyData}`);

export const getDailyCommentsAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getDailyComments}`,
    async(data: {
        careRecipientId: CareRecipientId,
        date: DateObject
    }, { rejectWithValue }) => {
        const response = await getDailyComments(data.careRecipientId, data.date);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { date: data.date.format(), dailyAlertCommentList: response.dailyAlertCommentList };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const resetDailyCommentsAction = createAction(`${ActionType.base}/${ActionType.resetDailyComments}`);

export const getMonthlyCommentsAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getMonthlyComments}`,
    async(data: {
        careRecipientId: CareRecipientId,
        month: Month
    }, { rejectWithValue }) => {
        const response = await getMonthlyComments(data.careRecipientId, data.month);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { monthlyComments: response };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const getDailyEstimatedBehaviorAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getDailyEstimatedBehavior}`,
    async(data: {
        careRecipientId: CareRecipientId,
        date: DateObject
    }, { rejectWithValue }) => {
        const response = await getDailyEstimatedBehavior(data.careRecipientId, data.date);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { ...response, date: data.date.format() };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }    }
);

export const getCareRecipientLatestInfoAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getCareRecipientLatestInfo}`,
    async(_data, { rejectWithValue }) => {
        const response = await getCareRecipientLatestInfo();

        if (response.error === undefined) {
            return response;
        } else {
            return rejectWithValue({});
        }
    }
);

export const getEstimatedBehaviorHistoriesAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getEstimatedBehaviorHistories}`,
    async(data: {
        careRecipientId: CareRecipientId,
        startDate: DateObject,
        endDate: DateObject
    }, { rejectWithValue }) => {
        const response = await getEstimatedBehaviorHistories(data.careRecipientId, data.startDate, data.endDate);

        if (response.error === undefined) {
            return {
                histories: {
                    toilet: response.histories.toilet,
                    bath: response.histories.bath,
                    goingOut: response.histories.goingOut,
                    sleep: response.histories.sleep,
                    meal: response.histories.meal
                }
            };
        } else {
            return rejectWithValue({});
        }
    }
);

export const getLastMonthEstimatedBehaviorHistoriesAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getLastMonthEstimatedBehaviorHistories}`,
    async(data: {
        careRecipientId: CareRecipientId,
        startDate: DateObject,
        endDate: DateObject
    }, { rejectWithValue }) => {
        const response = await getEstimatedBehaviorHistories(data.careRecipientId, data.startDate, data.endDate);

        if (response.error === undefined) {
            return {
                histories: {
                    toilet: response.histories.toilet,
                    bath: response.histories.bath,
                    goingOut: response.histories.goingOut,
                    sleep: response.histories.sleep,
                    meal: response.histories.meal
                }
            };
        } else {
            return rejectWithValue({});
        }
    }
);

export const setPeriodAction = createAction(
    `${ActionType.base}/${ActionType.setPeriod}`,
    function prepare(periodStartDate: DateObject, periodEndDate: DateObject) {
        return {
            payload: {
                periodStartDate: periodStartDate.format(),
                periodEndDate: periodEndDate.format()
            }
        };
    }
);

export const getEstimatedBehaviorAveragesAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getEstimatedBehaviorAverage}`,
    async(data: {
        careRecipientId: CareRecipientId
    }, { rejectWithValue }) => {
        const response = await getEstimatedBehaviorAverages(data.careRecipientId);
        if (response.error === undefined) {
            return {
                ...response
            };
        } else {
            return rejectWithValue({});
        }
    }
);

export const getEstimatedBehaviorAlertAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getEstimatedBehaviorAlert}`,
    async(data: {
        careRecipientId: CareRecipientId,
        date: DateObject
    }, { rejectWithValue }) => {
        const response = await getEstimatedBehaviorAlert(data.careRecipientId, data.date);
        if (response.error === undefined) {
            return {
                ...response
            };
        } else {
            return rejectWithValue({});
        }
    }
);

export const getEstimatedBehaviorAlertPeriodAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getEstimatedBehaviorAlertPeriod}`,
    async(data: {
        careRecipientId: CareRecipientId,
        startDate: DateObject,
        endDate: DateObject,
    }, { rejectWithValue }) => {
        const response = await getEstimatedBehaviorAlertPeriod(data.careRecipientId, data.startDate, data.endDate);
        if (response.error === undefined) {
            return {
                ...response
            };
        } else {
            return rejectWithValue({});
        }
    }
);